import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    inject,
    Input,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { translate } from '@jsverse/transloco';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { filter, includes, orderBy, union } from 'lodash-es';
import { NgTemplateOutlet } from '@angular/common';
import { InputComponent, SearchPipe, ButtonComponent } from 'vcp-common-ui-lib';
export interface PickListSelection<T> {
    source: T[];
    target: T[];
}

@Component({
    selector: 'vc-pick-list',
    templateUrl: './pick-list.component.html',
    styleUrls: ['./pick-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatSelectionList,
        CdkDropList,
        MatListOption,
        NgTemplateOutlet,
        CdkDrag,
        SearchPipe,
        InputComponent,
        SearchPipe,
        ButtonComponent,
    ],
})
export class PickListComponent<T> {
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    selectedSourceItem!: T[] | null;
    selectedTargetItem!: T[] | null;

    sourceSearch: string = '';
    targetSearch: string = '';

    sourceSortDirection: 'asc' | 'desc' | null = null;

    /** Initial array of items for source list */
    @Input()
    source!: T[];

    /** Initial array of items for target list */
    @Input()
    target!: T[];

    /** Whether to allow multiple selection option for source and target with checkbox [✓] */
    @Input()
    multiselect: boolean = false;

    /** Optionally set a header */
    @Input()
    header!: string;

    /** Function for rendering the source and target list items and supports filtering */
    @Input()
    itemRenderer: (data: T) => string = (value: T): string => `${value}`;

    /** Optionally set a header label for source listbox */
    @Input()
    sourceLabel!: string;

    /** Optionally set a header label for target listbox */
    @Input()
    targetLabel!: string;

    /** Sets the aria-label for the source listbox */
    @Input()
    sourceAriaLabel!: string;

    /** Sets the aria-label for the target listbox */
    @Input()
    targetAriaLabel!: string;

    /** Placeholder for source search input */
    @Input()
    sourceSearchLabel: string = translate('COMMON_UI.SEARCH');

    /** Placeholder for target search input */
    @Input()
    targetSearchLabel: string = translate('COMMON_UI.SEARCH');

    /** Placeholder for source search input */
    @Input()
    sourceSearchPlaceholder: string = '';

    /** Placeholder for target search input */
    @Input()
    targetSearchPlaceholder: string = '';

    /** Search key for source */
    @Input()
    sourceSearchKey: string = '';

    /** Search key for target */
    @Input()
    targetSearchKey: string = '';

    /** Whether to show search input for target */
    @Input()
    showTargetSearch: boolean = true;

    /** Whether to show search input for source */
    @Input()
    showSourceSearch: boolean = true;

    /** Whether to show move all to target button [>>] */
    @Input()
    showMoveAllToTarget: boolean = true;

    /** Whether to show move all to source button [<<] */
    @Input()
    showMoveAllToSource: boolean = true;

    /** Whether to allow management of moving items between Target and Source */
    @Input()
    manageMovementBetweenLists: boolean = true;

    /** Whether to show sortable column header for source list */
    @Input()
    showSourceSort: boolean = false;

    /** Column header label for source list sort */
    @Input()
    sourceColumnHeader: string = '';

    /** Key to sort source items by (supports nested keys like 'userKey.username') */
    @Input()
    sourceSortKey: string = '';

    /** Emits array of source/targets items after change */
    @Output()
    selectionChange = new EventEmitter<PickListSelection<T>>();

    /** Emits item moving from source to target */
    @Output()
    moveToTarget = new EventEmitter<T[] | null>();

    /** Emits item moving from target to source */
    @Output()
    moveToSource = new EventEmitter<T[] | null>();

    /** Emits array of items moving to target */
    @Output()
    moveAllToTarget = new EventEmitter<T[]>();

    /** Emits array of items moving to source */
    @Output()
    moveAllToSource = new EventEmitter<T[]>();

    @ViewChild('sourceList')
    sourceList!: MatSelectionList;

    @ViewChild('targetList')
    targetList!: MatSelectionList;

    @ContentChild('sourceItemTemplate')
    sourceItemTemplate!: TemplateRef<never>;

    @ContentChild('targetItemTemplate')
    targetItemTemplate!: TemplateRef<never>;

    get moveToTargetButtonDisabled(): boolean {
        return !this.selectedSourceItem || this.selectedSourceItem.length === 0;
    }

    get moveToSourceButtonDisabled(): boolean {
        return !this.selectedTargetItem || this.selectedTargetItem.length === 0;
    }

    get moveAllToTargetButtonDisabled(): boolean {
        return !this.source || this.source.length === 0;
    }

    get moveAllToSourceButtonDisabled(): boolean {
        return !this.target || this.target.length === 0;
    }

    get sortedSource(): T[] {
        if (!this.sourceSortDirection || !this.sourceSortKey) {
            return this.source;
        }
        return orderBy(this.source, [this.sourceSortKey], [this.sourceSortDirection]);
    }

    onSourceSort(): void {
        if (this.sourceSortDirection === null) {
            this.sourceSortDirection = 'asc';
        } else if (this.sourceSortDirection === 'asc') {
            this.sourceSortDirection = 'desc';
        } else {
            this.sourceSortDirection = null;
        }
        this._changeDetectorRef.markForCheck();
    }

    onSourceChange(event: MatSelectionListChange) {
        this.selectedSourceItem = event?.source?.selectedOptions?.selected?.map(
            (option: MatListOption) => option.value
        );
    }

    onTargetChange(event: MatSelectionListChange) {
        this.selectedTargetItem = event?.source?.selectedOptions?.selected?.map(
            (option: MatListOption) => option.value
        );
    }

    onMoveToTarget() {
        this.moveToTarget.emit(this.selectedSourceItem);
        if (!this.manageMovementBetweenLists) {
            return;
        }
        if (this.selectedSourceItem) {
            this.target = union(this.target, this.selectedSourceItem);
            this.source = filter(this.source, (value: T) => !includes(this.target, value));
        }
        this.sourceList?.deselectAll();
        this.selectedSourceItem = null;
        this.selectionChange.emit({ source: this.source, target: this.target });
        this._changeDetectorRef.markForCheck();
    }

    onMoveToSource() {
        this.moveToSource.emit(this.selectedTargetItem);
        if (!this.manageMovementBetweenLists) {
            return;
        }
        if (this.selectedTargetItem) {
            this.source = union(this.source, this.selectedTargetItem);
            this.target = filter(this.target, (value: T) => !includes(this.source, value));
        }
        this.targetList?.deselectAll();
        this.selectedTargetItem = null;
        this.selectionChange.emit({ source: this.source, target: this.target });
        this._changeDetectorRef.markForCheck();
    }

    onMoveAllToTarget() {
        this.moveAllToTarget.emit(this.sortedSource);
        if (!this.manageMovementBetweenLists) {
            return;
        }
        this.target = [...this.target, ...this.sortedSource];
        this.source = [];
        this.sourceList.deselectAll();
        this.selectedSourceItem = null;
        this.selectionChange.emit({ source: this.source, target: this.target });
        this._changeDetectorRef.markForCheck();
    }

    onMoveAllToSource() {
        this.moveAllToSource.emit(this.target);
        if (!this.manageMovementBetweenLists) {
            return;
        }
        this.source = [...this.source, ...this.target];
        this.target = [];
        this.targetList.deselectAll();
        this.selectedTargetItem = null;
        this.selectionChange.emit({ source: this.source, target: this.target });
        this._changeDetectorRef.markForCheck();
    }

    drop(event: CdkDragDrop<T[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
        this.selectionChange.emit({ source: this.source, target: this.target });
        this._changeDetectorRef.markForCheck();
    }
}
